import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/middlewares/auth/auth-context'
import { hasInvoiceAddress as isInvoiceAddressSet } from '@/utils/invoiceAddress'

/*
 * "Create an Event" opened in place on any page (mock _kit/create.html: every [data-create]
 * trigger — nav button, mobile menu, footer, home category bar, My Events bar — opens the same modal
 * over the current page). `view` is null | 'create' | 'gate': open() applies the "Complete your
 * organization account" gate first when the organizer has no invoice address yet. The modal itself
 * is rendered once by CreateEventHost in App.js.
 *
 * Logged out (15 Oct 2026, the button is now shown to visitors too): open() sends the visitor to
 * My Events with `?action=create-competition`. PrivateRoute bounces them to the login page, which
 * returns there afterwards, and My Events opens the modal on arrival.
 */
const CreateEventContext = React.createContext({
    view: null,
    open: () => {},
    close: () => {},
    setView: () => {},
})

export const LOGGED_OUT_CREATE_PATH = '/my-events?action=create-competition'

export function CreateEventProvider({ children }) {
    const authContext = useAuth()
    const navigate = useNavigate()
    const [view, setView] = useState(null)

    const isLoggedIn = authContext.isLoggedIn
    // the auth context's default settings carry an empty address object: truthy, but not an address
    const hasInvoiceAddress = isInvoiceAddressSet(authContext.user?.organisation_settings?.invoice_address)
    const open = useCallback(() => {
        if (!isLoggedIn) {
            navigate(LOGGED_OUT_CREATE_PATH)
            return
        }
        setView(hasInvoiceAddress ? 'create' : 'gate')
    }, [isLoggedIn, hasInvoiceAddress, navigate])
    const close = useCallback(() => setView(null), [])

    const value = useMemo(() => ({ view, setView, open, close }), [view, open, close])

    return <CreateEventContext.Provider value={value}>{children}</CreateEventContext.Provider>
}

export const useCreateEvent = () => useContext(CreateEventContext)
